import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { getCurrentCoords } from "../lib/geo";
import { StatusBadge } from "./StatusBadge";
import { MapView } from "./MapView";
import { FileUpload } from "./FileUpload";
import { SignatureCapture } from "./SignatureCapture";
import { Button } from "./ui/button";
import {
  Play,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  MapPin,
  Clock,
  Loader2,
  FileDown,
} from "lucide-react";
import { brl, fmtDateTime, durationMin, computeDurationMin, formatUSPhone } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { ContactActions } from "./ContactActions";
import { NotifyClientButton } from "./NotifyClientButton";
import { languageLabel } from "../lib/languages";
import { generateJobReceiptPDF } from "../lib/pdfReceipt";
import { toast } from "sonner";
import { logError } from "../lib/logger";

export function JobHeader({ job, client, team, onBack }) {
  const { t } = useI18n();
  return (
    <>
      <button
        onClick={onBack}
        data-testid="job-back"
        className="text-sm text-slate-500 hover:text-slate-900 mb-4 flex items-center gap-1"
      >
        <ArrowLeft size={14} /> {t("Voltar")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Limpeza")}
          </div>
          <h1 className="text-3xl font-bold">{client?.name || "—"}</h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <Clock size={14} /> {fmtDateTime(job.scheduled_at)}
          </div>
          <div className="text-sm text-slate-500 flex items-start gap-2">
            <MapPin size={14} className="mt-0.5" />
            <span>
              {client?.address}
              {client?.apt_number ? ` · Apt ${client.apt_number}` : ""}
              {client?.city || client?.state || client?.zip_code ? (
                <span className="block text-xs text-slate-400">
                  {[client.city, client.state, client.zip_code]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              ) : null}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={job.status} />
          <div className="text-xs text-slate-500">
            {t("Equipe:")} <strong>{team?.name}</strong>
          </div>
          {client?.language && (
            <div
              className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
              data-testid="job-client-language"
              title={t("Idioma falado pelo cliente")}
            >
              🗣 {languageLabel(client.language)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function JobDoorCodeCard({ doorCode }) {
  const { t } = useI18n();
  if (!doorCode) return null;
  return (
    <div className="cm-card p-4 mb-6 border-blue-300 bg-blue-50">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-lg">
          🔑
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Código da porta")}
          </div>
          <div
            className="text-2xl font-mono font-bold text-slate-900"
            data-testid="job-door-code"
          >
            {doorCode}
          </div>
        </div>
      </div>
    </div>
  );
}

export function JobObservationsCard({ observations }) {
  const { t } = useI18n();
  if (!observations) return null;
  return (
    <div className="cm-card p-4 mb-6 border-amber-300 bg-amber-50">
      <div className="flex items-start gap-2 text-amber-900">
        <AlertTriangle size={18} className="mt-0.5" />
        <div>
          <div className="font-semibold mb-1">{t("Observações importantes")}</div>
          <div className="text-sm whitespace-pre-line">{observations}</div>
        </div>
      </div>
    </div>
  );
}

export function JobActionPanel({ job, client, team, busy, isAdmin, onStart, onFinish, onSetStatus, onRevertToScheduled }) {
  const { t } = useI18n();
  const { user } = useAuth();
  async function handleDownloadReceipt() {
    try {
      toast.loading(t("Gerando comprovante…"), { id: "pdf" });
      // Colaboradores: PDF sem valor, sem telefone/e-mail e só o primeiro nome
      await generateJobReceiptPDF({
        job,
        client,
        team,
        privacyMode: user?.role === "collaborator",
      });
      toast.success(t("Comprovante baixado"), { id: "pdf" });
    } catch (e) {
      toast.error(t("Erro ao gerar PDF"), { id: "pdf" });
    }
  }

  return (
    <div className="cm-card p-6 text-center">
      {job.status === "agendada" || job.status === "deslocamento" ? (
        <>
          <div className="text-sm text-slate-500 mb-3">
            {t("Você precisa estar a até 200m do endereço para iniciar.")}
          </div>
          <Button
            data-testid="start-job-button"
            onClick={onStart}
            disabled={busy}
            className="w-full max-w-xs mx-auto h-16 text-lg bg-blue-600 hover:bg-blue-700 btn-pulse"
          >
            {busy ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                <Play size={22} className="mr-2" /> START
              </>
            )}
          </Button>
        </>
      ) : job.status === "andamento" ? (
        <>
          <div className="text-sm text-slate-500 mb-3">
            {t("Iniciada em")} {fmtDateTime(job.start_time)}
          </div>
          <Button
            data-testid="finish-job-button"
            onClick={onFinish}
            disabled={busy}
            className="w-full max-w-xs mx-auto h-16 text-lg bg-emerald-600 hover:bg-emerald-700"
          >
            {busy ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                <CheckCircle2 size={22} className="mr-2" /> {t("FINALIZAR")}
              </>
            )}
          </Button>
          {isAdmin && onRevertToScheduled && (
            <button
              type="button"
              onClick={onRevertToScheduled}
              disabled={busy}
              data-testid="revert-to-scheduled-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 hover:underline disabled:opacity-50"
              title={t("Reverter status para Agendada (admin)")}
            >
              {t("↩ Voltar para agendada (admin)")}
            </button>
          )}
        </>
      ) : (
        <div className="text-sm text-slate-600 py-4">
          {job.status === "finalizada"
            ? `${t("Finalizada em")} ${fmtDateTime(job.end_time)} · ${t("Duração")} ${durationMin(
                job.duration_minutes || computeDurationMin(job.start_time, job.end_time),
              )}`
            : t("Limpeza encerrada")}
          {job.status === "finalizada" && (
            <div className="mt-4">
              <Button
                data-testid="download-receipt-btn"
                onClick={handleDownloadReceipt}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                <FileDown size={16} className="mr-2" />
                {t("Baixar comprovante PDF")}
              </Button>
            </div>
          )}
        </div>
      )}

      {isAdmin && job.status !== "finalizada" && (
        <div className="flex flex-wrap justify-center gap-2 mt-4 text-xs">
          {[
            { key: "andamento", label: "START", className: "border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold" },
            { key: "agendada", label: "AGENDADA" },
            { key: "deslocamento", label: "DESLOCAMENTO" },
            { key: "andamento", label: "ANDAMENTO" },
            { key: "cancelada", label: "CANCELADA", className: "border-rose-300 text-rose-700 hover:bg-rose-50" },
          ].map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              onClick={() => onSetStatus(s.key)}
              data-testid={`set-status-${s.label.toLowerCase()}`}
              className={`px-3 py-1 rounded-full border text-slate-700 uppercase tracking-wide transition-colors ${
                s.className || "border-slate-200 hover:bg-slate-50"
              }`}
            >
              {t(s.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function JobChecklistCard({ checklist, jobStatus, onToggle }) {
  const { t } = useI18n();
  if (!checklist || checklist.length === 0) return null;
  const doneCount = checklist.filter((c) => c.done).length;
  const isLocked = jobStatus === "finalizada";
  return (
    <div className="cm-card p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          {t("📝 Checklist de limpeza")}
        </h3>
        <span className="text-xs text-slate-500">
          {doneCount}/{checklist.length} {t("concluídos")}
        </span>
      </div>
      <ul className="space-y-1.5">
        {checklist.map((it, i) => (
          <li
            key={`${i}-${it.text}`}
            data-testid={`job-checklist-${i}`}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
              it.done
                ? "bg-emerald-50 border-emerald-200"
                : "bg-white border-slate-200 hover:border-blue-300"
            } ${isLocked ? "pointer-events-none opacity-90" : ""}`}
            onClick={() => !isLocked && onToggle(i)}
          >
            <input
              type="checkbox"
              checked={!!it.done}
              readOnly
              className="w-5 h-5"
            />
            <span
              className={`flex-1 text-sm ${it.done ? "line-through text-slate-500" : "text-slate-800"}`}
            >
              {it.text}
            </span>
          </li>
        ))}
      </ul>
      {jobStatus === "andamento" && (
        <div className="mt-2 text-xs text-slate-500">
          {t("Toque em um item para marcar como concluído. Será salvo ao finalizar a limpeza.")}
        </div>
      )}
    </div>
  );
}

export function JobPhotosCard({ before, after, onBeforeChange, onAfterChange }) {
  const { t } = useI18n();
  return (
    <div className="cm-card p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">{t("Fotos do serviço")}</h3>
        <span className="text-xs text-slate-500 px-2 py-0.5 rounded-full bg-slate-100">
          {t("opcional")}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-2 text-sm flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            {t("Antes")}
          </h3>
          <FileUpload
            folder="before"
            value={before}
            onChange={onBeforeChange}
            testId="before-photos"
          />
        </div>
        <div>
          <h3 className="font-semibold mb-2 text-sm flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            {t("Depois")}
          </h3>
          <FileUpload
            folder="after"
            value={after}
            onChange={onAfterChange}
            testId="after-photos"
          />
        </div>
      </div>
    </div>
  );
}

export const JobSignatureCard = React.forwardRef(function JobSignatureCard(
  { signature, onSaved },
  ref,
) {
  const { t } = useI18n();
  return (
    <div className="cm-card p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">{t("Assinatura do cliente")}</h3>
        <span className="text-xs text-slate-500 px-2 py-0.5 rounded-full bg-slate-100">
          {t("opcional")}
        </span>
      </div>
      {signature ? (
        <div className="border border-slate-200 rounded-lg p-2 inline-block bg-white">
          <img src={fileUrl(signature)} alt="assinatura" className="h-32" />
          <div className="mt-2 text-xs text-slate-500 text-center">
            {t("Assinatura capturada")}
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2">
            {t("Peça ao cliente para assinar abaixo (ou pule este passo — será salva automaticamente ao finalizar se houver desenho).")}
          </p>
          <SignatureCapture ref={ref} onSaved={onSaved} />
        </>
      )}
    </div>
  );
});

export function JobSidebar({ job, client, team }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isCollaborator = user?.role === "collaborator";
  return (
    <div className="space-y-6">
      <div className="cm-card p-4">
        <h3 className="font-semibold mb-2">{t("Localização")}</h3>
        <MapView
          lat={client?.lat}
          lng={client?.lng}
          popup={client?.address}
          radius={200}
          height={220}
        />
      </div>

      {client?.phone && (
        <div className="cm-card p-4">
          <h3 className="font-semibold mb-2">{t("Contato do cliente")}</h3>
          <div className="text-sm font-mono text-slate-700 mb-3">
            +1 {formatUSPhone(client.phone)}
          </div>
          <ContactActions
            client={client}
            size="lg"
            testIdPrefix="job-contact"
          />
          <div className="mt-3">
            <NotifyClientButton
              client={client}
              job={job}
              team={team}
              testId="job-notify-client-btn"
            />
          </div>
        </div>
      )}

      {!isCollaborator && (
        <div className="cm-card p-4 text-sm">
          <h3 className="font-semibold mb-2">{t("Resumo financeiro")}</h3>
          <div className="flex justify-between py-1">
            <span className="text-slate-500">{t("Valor da limpeza")}</span>
            <strong>{brl(client?.value || 0)}</strong>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-slate-500">{t("Frequência")}</span>
            <span>{client?.frequency || "—"}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-slate-500">{t("Tipo")}</span>
            <span>{client?.property_type || "—"}</span>
          </div>
        </div>
      )}

      <div className="cm-card p-4 text-sm">
        <h3 className="font-semibold mb-2">{t("Equipe")}</h3>
        <div className="text-slate-700">{team?.name}</div>
        <div className="text-xs text-slate-500 mt-1">
          {team?.member_ids?.length || 0} {t("membro(s)")}
        </div>
      </div>
    </div>
  );
}


/**
 * Custom hook que centraliza toda a lógica de carregamento + ações de um Job.
 * Reusado por JobDetail.jsx (desktop) e MobileJobDetail.jsx (campo).
 */
export function useJobDetail(id) {
  const { t } = useI18n();
  const [job, setJob] = useState(null);
  const [client, setClient] = useState(null);
  const [team, setTeam] = useState(null);
  const [before, setBefore] = useState([]);
  const [after, setAfter] = useState([]);
  const [signature, setSignature] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [busy, setBusy] = useState(false);
  const signatureRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    setBefore(data.before_photos || []);
    setAfter(data.after_photos || []);
    setSignature(data.signature_path || null);

    // Backend já junta client + team na resposta. Usa diretamente quando disponível.
    if (data.client) {
      setClient(data.client);
      if (data.checklist && data.checklist.length) {
        setChecklist(data.checklist);
      } else if (data.client.cleaning_checklist?.length) {
        setChecklist(
          data.client.cleaning_checklist.map((text) => ({ text, done: false })),
        );
      }
    } else {
      // Fallback antigo: tenta buscar pelo id (caso endpoint não tenha enriquecido)
      api
        .get(`/clients/${data.client_id}`)
        .then((r) => {
          setClient(r.data);
          if (data.checklist && data.checklist.length) {
            setChecklist(data.checklist);
          } else if (r.data.cleaning_checklist?.length) {
            setChecklist(
              r.data.cleaning_checklist.map((text) => ({ text, done: false })),
            );
          }
        })
        .catch((e) => logError("Failed to load client:", e));
    }

    if (data.team) {
      setTeam(data.team);
    } else {
      api
        .get("/teams")
        .then((r) => setTeam(r.data.find((t) => t.id === data.team_id)))
        .catch((e) => logError("Failed to load teams:", e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function startJob() {
    setBusy(true);
    try {
      const { lat, lng } = await getCurrentCoords();
      await api.post(`/jobs/${id}/start`, { lat, lng });
      toast.success("Limpeza iniciada");
      load();
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Falha ao iniciar a limpeza";
      toast.error(formatApiError(msg));
    } finally {
      setBusy(false);
    }
  }

  async function finishJob() {
    setBusy(true);
    try {
      let sigPath = signature;
      if (!sigPath && signatureRef.current && !signatureRef.current.isEmpty()) {
        try {
          sigPath = await signatureRef.current.saveIfNotEmpty();
        } catch (e) {
          logError("Auto-save signature failed (continuing — optional):", e);
        }
      }
      await api.post(`/jobs/${id}/finish`, {
        before_photos: before,
        after_photos: after,
        signature_path: sigPath || null,
        checklist,
      });
      try {
        const r = await api.get(
          `/jobs?from=${new Date().toISOString().slice(0, 10)}T00:00:00&limit=200`,
        );
        const nxt = r.data.find((j) => j.auto_created_from === id);
        if (nxt) {
          toast.success(
            t("↻ Próxima limpeza agendada automaticamente para {date}", {
              date: new Date(nxt.scheduled_at).toLocaleDateString("en-US"),
            }),
            { duration: 5000 },
          );
        }
      } catch (e) {
        logError("Could not check next auto-job:", e);
      }
      toast.success(t("Limpeza finalizada"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(s) {
    try {
      await api.patch(`/jobs/${id}`, { status: s });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function revertToScheduled() {
    if (!window.confirm(
      t("Voltar este atendimento para 'Agendada'? Os dados de START (hora e GPS) serão apagados."),
    )) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/revert-to-scheduled`);
      toast.success(t("Atendimento revertido para agendada"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha"));
    } finally {
      setBusy(false);
    }
  }

  function toggleChecklistItem(idx) {
    setChecklist((arr) =>
      arr.map((x, i) => (i === idx ? { ...x, done: !x.done } : x)),
    );
  }

  return {
    job,
    client,
    team,
    before,
    after,
    signature,
    checklist,
    busy,
    signatureRef,
    setBefore,
    setAfter,
    setSignature,
    startJob,
    finishJob,
    setStatus,
    revertToScheduled,
    toggleChecklistItem,
  };
}
